import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiOutlineMoon, HiOutlineSun } from "react-icons/hi";
import { navLinks, socials } from "../constants/data";
import { useTheme } from "../context/ThemeContext";

const Clock = () => {
    const [time, setTime] = useState("");

    useEffect(() => {
        const tick = () =>
            setTime(
                new Date().toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                    timeZone: "Asia/Kolkata",
                })
            );
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    return (
        <span className="font-mono text-xs text-muted tabular-nums">
            NAINITAL, IN · {time} IST
        </span>
    );
};

const ThemeToggle = ({ className = "" }) => {
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === "dark";

    return (
        <button
            onClick={toggleTheme}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Light mode" : "Dark mode"}
            className={`flex items-center gap-2 rounded-full border border-line px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-muted transition-colors hover:border-accent hover:text-accent ${className}`}
        >
            {isDark ? <HiOutlineSun size={14} /> : <HiOutlineMoon size={14} />}
            <span className="hidden sm:inline">{isDark ? "Light" : "Dark"}</span>
        </button>
    );
};

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={`fixed top-0 z-50 w-full border-b transition-all duration-300 ${
                scrolled
                    ? "border-line bg-base/85 backdrop-blur-md"
                    : "border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
                <a
                    href="#top"
                    className="font-mono text-sm text-fg"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                    <span className="text-accent">~/</span>harshit.chaunal
                    <span className="cursor-blink ml-1 h-[0.9em] w-[0.45em]" />
                </a>

                <nav className="hidden items-center gap-7 md:flex">
                    {navLinks.map((link) => (
                        <a
                            key={link.id}
                            href={`#${link.id}`}
                            className="link-sweep pb-0.5 font-mono text-xs uppercase tracking-widest text-muted transition-colors hover:text-fg"
                        >
                            <span className="text-accent/70">{link.index}.</span>{" "}
                            {link.title}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden lg:block">
                        <Clock />
                    </div>
                    <ThemeToggle />
                    <a
                        href={socials.resume}
                        download
                        className="hidden rounded-full border border-line px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-fg transition-colors hover:border-accent hover:text-accent sm:inline-flex"
                    >
                        Résumé ↓
                    </a>

                    <button
                        onClick={() => setOpen(!open)}
                        className="font-mono text-xs uppercase tracking-widest text-fg md:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? "[ close ]" : "[ menu ]"}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden border-t border-line bg-base/95 backdrop-blur-md md:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {navLinks.map((link) => (
                                <a
                                    key={link.id}
                                    href={`#${link.id}`}
                                    onClick={() => setOpen(false)}
                                    className="py-2 font-mono text-sm uppercase tracking-widest text-muted hover:text-accent"
                                >
                                    <span className="text-accent/70">{link.index}.</span>{" "}
                                    {link.title}
                                </a>
                            ))}
                            <a
                                href={socials.resume}
                                download
                                className="mt-2 rounded-full border border-line px-3 py-2 text-center font-mono text-xs uppercase tracking-widest text-fg"
                            >
                                Résumé ↓
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;